import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  TemplateInputFieldEditorRow,
  type TemplateInputFieldEditorData,
} from "@/domains/templates/components/template-input-fields/template-input-field-editor-row";
import {
  createInputFieldEditorRow,
  inputFieldEditorRowToDefinition,
  type InputFieldEditorRow,
} from "@/domains/templates/input-fields";
import { inputFieldDefinitionsToVariables } from "@/domains/templates/mutations";
import { templateQueries } from "@/domains/templates/queries";
import type {
  InputFieldDefinition,
  TemplateVariableInput,
  TemplateVariableTarget,
  Variable,
} from "@/domains/templates/types";
import { useTemplateVariableMutations } from "../hooks/use-template-variable-mutations";
import {
  saveVariableHierarchy,
  type VariableHierarchyFailure,
} from "../lib/save-variable-hierarchy";
import { getErrorMessage } from "@/shared/lib/error-message";

type VariableDialogForm = {
  field: unknown;
};

type TemplateVariableDialogProps = {
  open: boolean;
  target: TemplateVariableTarget;
  variable?: Variable;
  variables: Variable[];
  selectableVariables: Variable[];
  editorData: TemplateInputFieldEditorData;
  loadChildren: (sourceVariableId: string) => Promise<Variable[]>;
  onOpenChange: (open: boolean) => void;
};

function variableDefinition(variable: Variable): InputFieldDefinition {
  return {
    key: variable.variable_name,
    type: variable.variable_type,
    description: variable.variable_description ?? undefined,
    source: variable.source_variable_id ? "variable" : "custom",
    variableId: variable.variable_id,
    sourceVariableId: variable.source_variable_id ?? undefined,
    variableNameFrontend: variable.variable_name_frontend,
    variableGroup: variable.variable_group,
    variableKind: variable.variable_kind,
    variableOptions: variable.variable_options,
    variableLinkedSchema: variable.variable_linked_schema,
    variableLinkedTable: variable.variable_linked_table,
    variableLinkedColumnId: variable.variable_linked_column_id,
    variableLinkedColumnName: variable.variable_linked_column_name,
    defaultValue: variable.variable_default,
    required: variable.variable_is_required,
    visible: variable.variable_is_visible,
    order: variable.variable_order,
    tier: variable.variable_tier,
    parentVariableId: variable.parent_variable_id,
  };
}

function toAtomicInput(
  definition: InputFieldDefinition,
): TemplateVariableInput {
  const input = (inputFieldDefinitionsToVariables([definition])[0] ??
    {}) as TemplateVariableInput & { variable_id?: string };
  const atomicInput = { ...input };
  delete atomicInput.variable_id;
  return atomicInput;
}

function canSave(field: InputFieldEditorRow): boolean {
  return field.source === "variable"
    ? Boolean(field.sourceVariableId)
    : Boolean(field.variableNameFrontend?.trim() || field.key.trim());
}

export function TemplateVariableDialog({
  open,
  target,
  variable,
  variables,
  selectableVariables,
  editorData,
  loadChildren,
  onOpenChange,
}: TemplateVariableDialogProps) {
  const mutations = useTemplateVariableMutations(target);
  const [failures, setFailures] = useState<VariableHierarchyFailure[]>([]);
  const [savedDuringAttempt, setSavedDuringAttempt] = useState<Variable[]>([]);
  const { watch, reset, setValue, handleSubmit } = useForm<VariableDialogForm>({
    defaultValues: { field: createInputFieldEditorRow() },
  });
  const field = watch("field") as InputFieldEditorRow;
  const source = useMemo(
    () =>
      selectableVariables.find(
        (candidate) => candidate.variable_id === field.sourceVariableId,
      ),
    [field.sourceVariableId, selectableVariables],
  );
  const linkedTable =
    field.variableLinkedTable ?? source?.variable_linked_table ?? null;
  const { data: linkedAnalyses = [], isLoading: linkedAnalysesLoading } =
    useQuery({
      ...templateQueries.linkedAnalyses(),
      enabled: open && linkedTable === "analysis",
    });
  const {
    data: linkedAnalysisSteps = [],
    isLoading: linkedAnalysisStepsLoading,
  } = useQuery({
    ...templateQueries.linkedAnalysisSteps(target.templateId),
    enabled: open && linkedTable === "analysisstep",
  });
  const resolvedEditorData = {
    ...editorData,
    linkedAnalyses,
    linkedAnalysisSteps,
    isLoading:
      editorData.isLoading ||
      linkedAnalysesLoading ||
      linkedAnalysisStepsLoading,
  };
  const isPending =
    mutations.createVariable.isPending ||
    mutations.createVariableHierarchy.isPending ||
    mutations.updateVariable.isPending;

  useEffect(() => {
    if (!open) return;
    reset({
      field: createInputFieldEditorRow(
        variable ? variableDefinition(variable) : undefined,
      ),
    });
    setFailures([]);
    setSavedDuringAttempt([]);
  }, [open, reset, variable]);

  const save = handleSubmit(async ({ field: rawDraftField }) => {
    const draftField = rawDraftField as InputFieldEditorRow;
    setFailures([]);
    const definition = inputFieldEditorRowToDefinition(draftField);
    let currentVariables = [...variables, ...savedDuringAttempt];
    let canonicalVariable = variable;

    if (variable) {
      try {
        canonicalVariable = await mutations.updateVariable.mutateAsync({
          variableId: variable.variable_id,
          variable: toAtomicInput(definition),
        });
      } catch (error) {
        setFailures([
          {
            sourceVariableId:
              variable.source_variable_id ?? variable.variable_id,
            name: variable.variable_name_frontend ?? variable.variable_name,
            error,
          },
        ]);
        return;
      }
      currentVariables = [
        ...currentVariables.filter(
          (item) => item.variable_id !== canonicalVariable?.variable_id,
        ),
        canonicalVariable,
      ];
    }

    const result = await saveVariableHierarchy({
      target,
      draft: {
        ...definition,
        variableId: canonicalVariable?.variable_id,
      },
      source,
      existing: currentVariables,
      loadChildren,
      createVariable: (input) =>
        mutations.createVariable.mutateAsync({ variable: input }),
      createHierarchy: (input) =>
        mutations.createVariableHierarchy.mutateAsync({ variable: input }),
    });
    setSavedDuringAttempt(result.saved);
    setFailures(result.failed);
    if (result.failed.length === 0) onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {variable ? "Edit variable" : "Add variable"}
          </DialogTitle>
          <DialogDescription>
            Changes are sent only after you press Save.
          </DialogDescription>
        </DialogHeader>
        <TemplateInputFieldEditorRow
          field={field}
          index={0}
          variableScope={target.level === "analysis" ? "catalog" : "analysis"}
          analysisId={target.templateId}
          data={resolvedEditorData}
          isDraft
          layout="form"
          onChange={(_index, nextField) =>
            setValue(
              "field",
              { ...nextField, tier: "standard" },
              { shouldDirty: true },
            )
          }
        />
        {failures.length > 0 && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 p-3 text-sm text-destructive"
          >
            <p>Could not save:</p>
            <ul className="list-disc pl-5">
              {failures.map((failure) => (
                <li key={failure.sourceVariableId}>
                  {failure.name}:{" "}
                  <span>
                    {getErrorMessage(failure.error, "Could not save variable")}
                  </span>
                </li>
              ))}
            </ul>
            <p>
              Successful variables were kept. Retry saves only the missing
              items.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSave(field) || isPending}
            onClick={() => void save()}
          >
            {isPending
              ? "Saving…"
              : failures.length > 0
                ? "Retry missing"
                : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
